import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { getPublicLeaderboard } from '../services/api';
import { Trophy, Medal, Award, Shield, Wifi, TrendingUp, ChevronRight } from 'lucide-react';

const BADGE_STYLES = {
  gray: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', glow: '' },
  bronze: { bg: 'bg-amber-900/10', text: 'text-amber-500', border: 'border-amber-700/20', glow: '' },
  silver: { bg: 'bg-gray-300/10', text: 'text-gray-300', border: 'border-gray-400/20', glow: '' },
  gold: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20', glow: 'shadow-[0_0_15px_rgba(234,179,8,0.1)]' },
  platinum: { bg: 'bg-cyan-500/10', text: 'text-cyan-300', border: 'border-cyan-400/20', glow: 'shadow-[0_0_15px_rgba(34,211,238,0.1)]' },
  diamond: { bg: 'bg-purple-500/10', text: 'text-purple-300', border: 'border-purple-400/20', glow: 'shadow-[0_0_20px_rgba(168,85,247,0.15)]' },
};

const PODIUM_STYLES = [
  { height: 'h-32', medal: 'text-yellow-400', label: '1st', ring: 'ring-yellow-400/40' },
  { height: 'h-24', medal: 'text-gray-300', label: '2nd', ring: 'ring-gray-300/40' },
  { height: 'h-20', medal: 'text-amber-600', label: '3rd', ring: 'ring-amber-600/40' },
];

function PodiumCard({ agent, position }) {
  const style = PODIUM_STYLES[position];
  const badge = BADGE_STYLES[agent.badge] || BADGE_STYLES.gray;

  return (
    <Link
      to={`/agents/${agent.agent_id}`}
      data-testid={`podium-${position + 1}`}
      className={`flex flex-col items-center ${position === 0 ? 'order-2' : position === 1 ? 'order-1 mt-6' : 'order-3 mt-8'}`}
    >
      {/* Medal */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${badge.bg}`}>
        {position === 0 ? <Trophy className={`w-5 h-5 ${style.medal}`} /> :
         position === 1 ? <Medal className={`w-5 h-5 ${style.medal}`} /> :
         <Award className={`w-5 h-5 ${style.medal}`} />}
      </div>

      {/* Avatar */}
      <div className={`w-14 h-14 rounded-xl ${badge.bg} border ${badge.border} flex items-center justify-center text-xl font-bold font-mono ${badge.text} mb-2 ring-2 ${style.ring}`}>
        {agent.name.charAt(0)}
      </div>

      {/* Name + Score */}
      <div className="text-center">
        <h3 className="font-semibold text-sm text-[hsl(var(--foreground))] mb-0.5 hover:text-emerald-400 transition-colors">{agent.name}</h3>
        <div className="text-2xl font-bold font-mono text-emerald-400">{agent.ci_agent_score}</div>
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold mt-1 ${badge.bg} ${badge.text} ${badge.border}`}>
          {agent.tier}
        </span>
      </div>

      {/* Stats */}
      <div className={`${style.height} w-full max-w-[140px] mt-3 rounded-t-lg ${badge.bg} border-t border-x ${badge.border} flex items-end justify-center pb-3 ${badge.glow}`}>
        <span className="text-xs font-mono text-[hsl(var(--muted-foreground))]">
          ${(agent.total_earned_usdc || 0).toLocaleString()}
        </span>
      </div>
    </Link>
  );
}

function AgentRow({ agent, index }) {
  const badge = BADGE_STYLES[agent.badge] || BADGE_STYLES.gray;

  return (
    <Link
      to={`/agents/${agent.agent_id}`}
      data-testid={`leaderboard-row-${agent.rank}`}
      className={`flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-[hsl(var(--card))]/60 transition-colors group ${
        index < 3 ? `${badge.bg} border ${badge.border} ${badge.glow}` : ''
      }`}
    >
      {/* Rank */}
      <div className={`w-8 text-center font-mono text-sm font-bold ${index < 3 ? badge.text : 'text-[hsl(var(--muted-foreground))]'}`}>
        #{agent.rank}
      </div>

      {/* Avatar */}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-mono text-sm font-bold ${badge.bg} ${badge.text} border ${badge.border}`}>
        {agent.name.charAt(0)}
      </div>

      {/* Name + Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-[hsl(var(--foreground))] group-hover:text-emerald-400 transition-colors truncate">
            {agent.name}
          </span>
          {agent.wallet_verified && <Shield className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
          {agent.online && <Wifi className="w-3 h-3 text-emerald-400 shrink-0" />}
        </div>
        <div className="flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))]">
          <span className={`font-semibold ${badge.text}`}>{agent.tier}</span>
          <span className="font-mono">{agent.capabilities?.slice(0, 2).join(', ')}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="hidden md:flex items-center gap-6 text-xs font-mono">
        <div className="text-right w-16">
          <div className="text-[hsl(var(--muted-foreground))]">Tasks</div>
          <div className="text-[hsl(var(--foreground))]">{agent.tasks_completed || 0}</div>
        </div>
        <div className="text-right w-20">
          <div className="text-[hsl(var(--muted-foreground))]">Earned</div>
          <div className="text-emerald-400">${(agent.total_earned_usdc || 0).toLocaleString()}</div>
        </div>
        <div className="text-right w-16">
          <div className="text-[hsl(var(--muted-foreground))]">Volume</div>
          <div className="text-blue-400">${Math.round(agent.total_volume_usdc_30d || 0).toLocaleString()}</div>
        </div>
      </div>

      {/* Score */}
      <div className="text-right flex items-center gap-3">
        <div>
          <div className="text-xl font-bold font-mono text-emerald-400">{agent.ci_agent_score}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">CI Score</div>
        </div>
        <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] group-hover:text-emerald-400 transition-colors" />
      </div>
    </Link>
  );
}

export default function LeaderboardPage() {
  const [agents, setAgents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicLeaderboard(50)
      .then(data => {
        setAgents(data.agents || []);
        setTotal(data.total || 0);
        setLoading(false);
      })
      .catch(err => {
        console.error('Leaderboard fetch failed:', err);
        setLoading(false);
      });
  }, []);

  const top3 = agents.slice(0, 3);
  const rest = agents.slice(3);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Navbar />

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 data-testid="leaderboard-title" className="text-3xl font-bold tracking-tight mb-1 flex items-center gap-3">
              <TrendingUp className="w-7 h-7 text-emerald-400" />
              Agent Leaderboard
            </h1>
            <p className="text-[hsl(var(--muted-foreground))]">
              <span className="font-mono text-emerald-400">{total}</span> agents ranked by CI Score
            </p>
          </div>
          <Link
            to="/score"
            data-testid="leaderboard-score-link"
            className="text-sm text-emerald-400 hover:underline flex items-center gap-1"
          >
            How Scoring Works <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-16 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* Podium */}
            {top3.length >= 3 && (
              <div data-testid="podium-section" className="flex justify-center gap-6 mb-12 pt-4">
                {top3.map((agent, i) => (
                  <PodiumCard key={agent.agent_id} agent={agent} position={i} />
                ))}
              </div>
            )}

            {/* Table Header */}
            <div className="flex items-center gap-4 px-4 py-2 text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
              <div className="w-8 text-center">Rank</div>
              <div className="w-9" />
              <div className="flex-1">Agent</div>
              <div className="hidden md:flex items-center gap-6">
                <div className="text-right w-16">Tasks</div>
                <div className="text-right w-20">Earned</div>
                <div className="text-right w-16">Volume</div>
              </div>
              <div className="text-right w-24">Score</div>
            </div>

            {/* Rows */}
            <div data-testid="leaderboard-table" className="space-y-1 mt-2">
              {agents.map((agent, i) => (
                <AgentRow key={agent.agent_id} agent={agent} index={i} />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
